import React, { useMemo, useState, useRef, useLayoutEffect, useEffect, useCallback } from "react";
import "./FilterMenu.css";

/**
 * Column dropdown — the Excel-style combined Sort + Filter menu.
 *
 * Layout, top → bottom:
 *   1. Sort row     — segmented A–Z / Z–A toggle + Custom + Clear
 *   2. Search box   — inline search icon + clear (×)
 *   3. Select-all   — with a live "selected / total" count
 *   4. Value list   — custom-styled checkboxes
 *   5. Footer       — "N of M selected" + Cancel / Apply
 *
 * NOTE on class names: every class here is prefixed `sev-filter-…`
 * (SimpleExcelViewer filter) so they can never collide with chart/axis
 * or any other global styles. Do not reuse these names elsewhere.
 */
function FilterMenu({
  anchorEl, colIndex, rows, currentFilter, onApply, onClose,
  sortDir, onSortAsc, onSortDesc, onClearSort, onCustomSort,
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const popupRef = useRef(null);
  // The popup is portalled to <body> and positioned `fixed` relative to the
  // button (anchorEl).  Hidden until measured so it never flashes at 0,0.
  const [posStyle, setPosStyle] = useState({ position: "fixed", top: 0, left: 0, visibility: "hidden" });

  const allValues = useMemo(() => {
    const vals = new Set();
    // Skip row 0 (header row) — it should not appear as a filterable value
    rows.forEach((row, idx) => {
      if (idx === 0) return;
      const v = row[colIndex];
      vals.add(v == null || v === "" ? "(Blank)" : String(v));
    });
    const arr = Array.from(vals);
    // Sort numerically for number values, then alphabetically for text
    arr.sort((a, b) => {
      const aNum = Number(a);
      const bNum = Number(b);
      const aIsNum = a !== "(Blank)" && a !== "" && !isNaN(aNum);
      const bIsNum = b !== "(Blank)" && b !== "" && !isNaN(bNum);
      if (aIsNum && bIsNum) return aNum - bNum;
      if (aIsNum) return -1;
      if (bIsNum) return 1;
      return a.localeCompare(b);
    });
    return arr;
  }, [colIndex, rows]);

  const [selected, setSelected] = useState(() => {
    if (currentFilter) return new Set(currentFilter);
    return new Set(allValues);
  });

  const filteredValues = useMemo(() => {
    if (!searchTerm) return allValues;
    const lower = searchTerm.toLowerCase();
    return allValues.filter(v => v.toLowerCase().includes(lower));
  }, [allValues, searchTerm]);

  const isAllFilteredSelected = filteredValues.length > 0 && filteredValues.every(v => selected.has(v));

  const filteredSelectedCount = useMemo(
    () => filteredValues.reduce((n, v) => n + (selected.has(v) ? 1 : 0), 0),
    [filteredValues, selected],
  );

  const toggleAll = () => {
    const next = new Set(selected);
    if (isAllFilteredSelected) filteredValues.forEach(v => next.delete(v));
    else filteredValues.forEach(v => next.add(v));
    setSelected(next);
  };

  const toggleValue = (val) => {
    const next = new Set(selected);
    if (next.has(val)) next.delete(val);
    else next.add(val);
    setSelected(next);
  };

  const runSort = (fn) => { if (fn) fn(colIndex); onClose(); };
  const apply = () => {
    if (searchTerm) {
      // Excel "search then OK" semantics: filter to the values that match
      // the search AND are checked, dropping everything else (as Excel does
      // with "Add current selection to filter" left off).  This is what makes
      // the search actually filter the grid instead of being cosmetic.
      const eff = new Set(filteredValues.filter(v => selected.has(v)));
      onApply(eff.size === allValues.length ? null : eff);
    } else {
      onApply(selected.size === allValues.length ? null : selected);
    }
    onClose();
  };
  const stop = (e) => { e.stopPropagation(); e.nativeEvent.stopImmediatePropagation(); };

  const hasSort = onSortAsc || onSortDesc;

  // Position the portalled popup under the button, clamped to the viewport
  // (shift left if it would overflow the right edge; flip above the button if
  // it would overflow the bottom).
  const reposition = useCallback(() => {
    const el = popupRef.current;
    if (!el || !anchorEl) return;
    const a = anchorEl.getBoundingClientRect();
    const pw = el.offsetWidth;
    const ph = el.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const m = 6;
    let left = a.left;
    let top = a.bottom + 3;
    if (left + pw > vw - m) left = vw - m - pw;        // overflow right → shift left
    if (left < m) left = m;
    if (top + ph > vh - m) {                            // not enough room below
      const up = a.top - 3 - ph;                        // try above the button
      if (up >= m) top = up;
    }
    // Keep fully on-screen even as the button scrolls toward an edge, so the
    // popup stays visible/usable instead of sliding off with its button.
    top = Math.max(m, Math.min(top, vh - m - ph));
    left = Math.max(m, Math.min(left, vw - m - pw));
    setPosStyle({ position: "fixed", top: Math.round(top), left: Math.round(left), visibility: "visible" });
  }, [anchorEl]);

  // Position now, then again on the next frame.  The first pass can measure a
  // stale height (before the list's final layout / fonts settle), which left
  // the popup clamped so its bottom (the Apply button) sat off-screen; the
  // second pass re-measures the settled height and clamps it fully on-screen.
  useLayoutEffect(() => {
    reposition();
    const id = requestAnimationFrame(reposition);
    return () => cancelAnimationFrame(id);
  }, [reposition]);

  // Keep the popup glued to its button as the grid scrolls/resizes — DON'T
  // close it, so you can scroll to read the data while the filter is open.
  // (Capture phase catches the grid's inner scroll container.)
  useEffect(() => {
    let raf = 0;
    const onMove = (e) => {
      const p = popupRef.current;
      // Ignore scrolling that happens INSIDE the popup (its own value list).
      // Only the grid/page scrolling should move the popup — repositioning on
      // the list's own scroll caused a re-render on every scroll frame.
      const t = e && e.target;
      if (t && t.nodeType && p && p.contains(t)) return;
      // Ignore scrolls that fire while focus is INSIDE the popup.  Clicking a
      // value checkbox focuses that native <input>, and the browser's "scroll
      // the focused element into view" can nudge the grid/window (a
      // programmatic scroll that overscroll-behavior can't stop).  Without
      // this guard that nudge triggered reposition() and the popup jumped when
      // selecting an item after scrolling.  Real grid-reading scrolls happen
      // with focus outside the popup, so they still keep it glued.
      if (p && p.contains(document.activeElement)) return;
      if (raf) return;
      raf = requestAnimationFrame(() => { raf = 0; reposition(); });
    };
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => {
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [reposition]);

  // Outside-click close.  Because the popup is portalled to <body>, a simple
  // window-click listener can't tell inside from outside via bubbling — so we
  // check DOM containment against the popup and its anchor button instead.
  useEffect(() => {
    const onDocDown = (e) => {
      const p = popupRef.current;
      if (p && p.contains(e.target)) return;             // click inside popup
      if (anchorEl && anchorEl.contains(e.target)) return; // click on the button
      onClose();
    };
    document.addEventListener("mousedown", onDocDown, true);
    return () => document.removeEventListener("mousedown", onDocDown, true);
  }, [anchorEl, onClose]);

  return (
    <div
      ref={popupRef}
      className="sev-filter-popup"
      role="dialog"
      aria-label="Sort and filter column"
      style={posStyle}
      onClick={stop}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Sticky header group: sort + search + select-all stay pinned at the top
          while the value list scrolls beneath them (see .sev-filter-head CSS). */}
      <div className="sev-filter-head">
      {hasSort && (
        <div className="sev-filter-sort">
          <div className="sev-filter-seggroup">
            <button
              type="button"
              className={`sev-filter-seg ${sortDir === "asc" ? "on" : ""}`}
              onClick={() => runSort(onSortAsc)}
              title="Sort A to Z"
            >
              <span className="material-icons">arrow_upward</span><span>A–Z</span>
            </button>
            <button
              type="button"
              className={`sev-filter-seg ${sortDir === "desc" ? "on" : ""}`}
              onClick={() => runSort(onSortDesc)}
              title="Sort Z to A"
            >
              <span className="material-icons">arrow_downward</span><span>Z–A</span>
            </button>
          </div>
          {onCustomSort && (
            <button type="button" className="sev-filter-iconbtn" title="Custom (multi-level) sort" onClick={() => { onCustomSort(); onClose(); }}>
              <span className="material-icons">sort_by_alpha</span>
            </button>
          )}
          <button type="button" className="sev-filter-iconbtn" title="Clear sort" disabled={!sortDir} onClick={() => runSort(onClearSort)}>
            <span className="material-icons">clear_all</span>
          </button>
        </div>
      )}

      <div className="sev-filter-search">
        <span className="material-icons sev-filter-search-ic">search</span>
        <input
          type="text"
          className="sev-filter-search-input"
          placeholder="Search"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          autoFocus
        />
        {searchTerm && (
          <button type="button" className="sev-filter-search-x" title="Clear search" onClick={() => setSearchTerm("")}>
            <span className="material-icons">close</span>
          </button>
        )}
      </div>

      <label className="sev-filter-all" onMouseDown={(e) => e.preventDefault()}>
        <input type="checkbox" className="sev-filter-cbx" checked={isAllFilteredSelected} onChange={toggleAll} />
        <span className="sev-filter-box" aria-hidden="true" />
        <span className="sev-filter-all-text">{searchTerm ? "Select all results" : "Select all"}</span>
        <span className="sev-filter-count">
          {searchTerm ? filteredSelectedCount : selected.size}/{searchTerm ? filteredValues.length : allValues.length}
        </span>
      </label>
      </div>

      <div className="sev-filter-list">
        {filteredValues.map(val => (
          // onMouseDown preventDefault: stop the native <input> from taking
          // focus on click.  The browser's "scroll focused element into view"
          // was scrolling the grid/window itself (not just the popup), which
          // is what made the view jump when selecting an item after scrolling.
          // The checkbox still toggles via onChange; focus stays put.
          <label key={val} className="sev-filter-item" onMouseDown={(e) => e.preventDefault()}>
            <input type="checkbox" className="sev-filter-cbx" checked={selected.has(val)} onChange={() => toggleValue(val)} />
            <span className="sev-filter-box" aria-hidden="true" />
            <span className={`sev-filter-item-text ${val === "(Blank)" ? "is-blank" : ""}`} title={val}>
              {val === "(Blank)" ? "(Blanks)" : val}
            </span>
          </label>
        ))}
        {filteredValues.length === 0 && (
          <div className="sev-filter-empty">No matching values</div>
        )}
      </div>

      <div className="sev-filter-footer">
        {/* NOTE (large sheets): with thousands of rows the count text can get
            long (e.g. "12,345 of 12,345"). The footer CSS lets this text
            shrink/ellipsis so Cancel/Apply stay fully visible. Separately, the
            value list renders every distinct value — for very large sheets it
            should be windowed/virtualized (follow-up perf item). */}
        <span
          className="sev-filter-fcount"
          title={searchTerm
            ? `${filteredSelectedCount} of ${filteredValues.length} matches selected`
            : `${selected.size} of ${allValues.length} selected`}
        >
          {searchTerm
            ? `${filteredSelectedCount.toLocaleString()} of ${filteredValues.length.toLocaleString()} matches`
            : `${selected.size.toLocaleString()} of ${allValues.length.toLocaleString()} selected`}
        </span>
        <span className="sev-filter-fbtns">
          <button type="button" className="sev-filter-btn sev-filter-btn-cancel" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="sev-filter-btn sev-filter-btn-ok"
            disabled={searchTerm ? filteredSelectedCount === 0 : selected.size === 0}
            aria-disabled={searchTerm ? filteredSelectedCount === 0 : selected.size === 0}
            onClick={apply}
          >
            Apply
          </button>
        </span>
      </div>
    </div>
  );
}

export default FilterMenu;
